/**
 * Quiz reminders — opt-in browser notifications for upcoming quizzes.
 * Opt-ins are stored in localStorage; while the app is open, a checker fires a
 * browser Notification at each threshold (24h and 1h before the deadline),
 * once per quiz+threshold.
 */
import { UpcomingQuizItem } from '../api/courses';

const OPTIN_KEY = 'classrooms-quiz-reminders';
const FIRED_KEY = 'classrooms-quiz-reminders-fired';

// Milliseconds before the deadline at which to notify
const THRESHOLDS: { label: string; ms: number }[] = [
  { label: '24h', ms: 24 * 3_600_000 },
  { label: '1h', ms: 3_600_000 },
];

const readSet = (key: string): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || '[]'));
  } catch {
    return new Set();
  }
};

const writeSet = (key: string, s: Set<string>) => {
  localStorage.setItem(key, JSON.stringify(Array.from(s)));
};

export const isReminderSet = (quizId: string): boolean => readSet(OPTIN_KEY).has(quizId);

/** Toggle the opt-in for a quiz; returns the new state. */
export const toggleReminder = (quizId: string): boolean => {
  const optins = readSet(OPTIN_KEY);
  if (optins.has(quizId)) {
    optins.delete(quizId);
  } else {
    optins.add(quizId);
  }
  writeSet(OPTIN_KEY, optins);
  return optins.has(quizId);
};

/** Ask for Notification permission if needed; true when granted. */
export const ensureNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
};

/**
 * Check opted-in quizzes against their deadlines and fire due notifications.
 * Safe to call repeatedly; each quiz+threshold fires at most once.
 */
export const checkReminders = (items: UpcomingQuizItem[]) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const optins = readSet(OPTIN_KEY);
  const fired = readSet(FIRED_KEY);
  const now = Date.now();
  let changed = false;

  for (const item of items) {
    if (!optins.has(item.quiz_id)) continue;
    const deadline = new Date(item.next_deadline).getTime();
    const msLeft = deadline - now;
    if (msLeft <= 0) continue;
    for (const t of THRESHOLDS) {
      const key = `${item.quiz_id}:${t.label}`;
      if (msLeft <= t.ms && !fired.has(key)) {
        const verb = item.status === 'scheduled' ? 'opens' : 'is due';
        const hoursLeft = Math.max(1, Math.round(msLeft / 3_600_000));
        new Notification(`${item.title} ${verb} soon`, {
          body: `${item.course_title}: ${verb === 'opens' ? 'opens' : 'due'} in about ${hoursLeft} ${hoursLeft === 1 ? 'hour' : 'hours'}.`,
          icon: '/logo192.png',
          tag: key,
        });
        fired.add(key);
        changed = true;
      }
    }
  }
  if (changed) writeSet(FIRED_KEY, fired);
};

/** Start the periodic reminder checker; returns a cleanup function. */
export const startReminderChecker = (getItems: () => UpcomingQuizItem[]): (() => void) => {
  checkReminders(getItems());
  const id = setInterval(() => checkReminders(getItems()), 60_000);
  return () => clearInterval(id);
};
